import axios from "axios";

// In Docker/production, requests are relative so Nginx proxies /api/ → backend.
// In local dev (npm run dev), Vite's proxy handles /api/ → http://localhost:8000.
// Override with VITE_API_URL only when calling the backend directly (e.g. a different host).
const BASE_URL = import.meta.env.VITE_API_URL ?? "";

const SESSION_KEY = "app_secret";

export const getStoredSecret = (): string => sessionStorage.getItem(SESSION_KEY) ?? "";
export const setStoredSecret = (secret: string): void => sessionStorage.setItem(SESSION_KEY, secret);
export const clearStoredSecret = (): void => sessionStorage.removeItem(SESSION_KEY);

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Attach the stored secret before every request.
api.interceptors.request.use((config) => {
  const secret = getStoredSecret();
  if (secret) {
    config.headers.Authorization = `Bearer ${secret}`;
  }
  return config;
});

// ---- Types ----

export interface Portfolio {
  id: number;
  name: string;
  account_id: string;
  currency: string;
  ibkr_flex_token: string | null;
  ibkr_flex_query_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Trade {
  id: number;
  portfolio_id: number;
  trade_id: string;
  symbol: string;
  description?: string;
  asset_class?: string;
  currency: string;
  trade_date: string;
  settle_date?: string;
  action: string;
  quantity: number;
  price: number;
  proceeds?: number;
  commission?: number;
  taxes?: number;
  realized_pnl?: number;
  fx_rate?: number;
  exchange?: string;
  order_id?: string;
  created_at: string;
}

export interface Position {
  id: number;
  portfolio_id: number;
  symbol: string;
  description?: string;
  asset_class?: string;
  currency: string;
  quantity: number;
  cost_basis?: number;
  market_price?: number;
  market_value?: number;
  unrealized_pnl?: number;
  realized_pnl?: number;
  report_date: string;
  created_at: string;
  updated_at: string;
}

export interface EvolutionPoint {
  date: string;
  total_value: number;
  unrealized_pnl: number;
  realized_pnl: number;
}

export interface SymbolPerformance {
  symbol: string;
  total_quantity: number;
  total_proceeds: number;
  total_commission: number;
  realized_pnl: number;
  unrealized_pnl: number;
  trade_count: number;
}

export interface CurrentPosition {
  symbol: string;
  description?: string;
  asset_class?: string;
  currency: string;
  quantity: number;
  cost_basis: number;
  market_price: number;
  market_value: number;
  unrealized_pnl: number;
  realized_pnl: number;
  report_date: string;
  latest_market_data_date?: string;
}

// ---- Portfolio API ----

export const getPortfolios = () => api.get<Portfolio[]>("/api/v1/portfolios/");

export const createPortfolio = (data: {
  name: string;
  account_id: string;
  currency?: string;
}) => api.post<Portfolio>("/api/v1/portfolios/", data);

export const updatePortfolio = (id: number, data: { name?: string; currency?: string; ibkr_flex_token?: string | null; ibkr_flex_query_id?: string | null }) =>
  api.patch<Portfolio>(`/api/v1/portfolios/${id}`, data);

export const deletePortfolio = (id: number) =>
  api.delete(`/api/v1/portfolios/${id}`);

export const clearPortfolioPositions = (id: number) =>
  api.delete<{ deleted: number }>(`/api/v1/portfolios/${id}/positions`);

export const clearPortfolioTrades = (id: number) =>
  api.delete<{ deleted: number }>(`/api/v1/portfolios/${id}/trades`);

// ---- Trade API ----

export const getTrades = (params: {
  portfolio_id?: number;
  symbol?: string;
  start_date?: string;
  end_date?: string;
  action?: string;
  skip?: number;
  limit?: number;
}) => api.get<Trade[]>("/api/v1/trades/", { params });

// ---- Position API ----

export const getPositions = (params: {
  portfolio_id?: number;
  symbol?: string;
  report_date?: string;
  skip?: number;
  limit?: number;
}) => api.get<Position[]>("/api/v1/positions/", { params });

// ---- Analytics API ----

export const getPortfolioEvolution = (
  portfolioId: number,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<EvolutionPoint[]>(`/api/v1/analytics/${portfolioId}/evolution`, {
    params,
  });

export const getSymbolPerformance = (portfolioId: number) =>
  api.get<SymbolPerformance[]>(`/api/v1/analytics/${portfolioId}/performance`);

export const getCurrentPositions = (portfolioId: number) =>
  api.get<CurrentPosition[]>(`/api/v1/analytics/${portfolioId}/positions`);

// ---- IBKR Import API ----

export const uploadFlexXml = (portfolioId: number, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/api/v1/ibkr/upload/${portfolioId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const fetchFlexFromIbkr = (portfolioId: number) =>
  api.post(`/api/v1/ibkr/fetch/${portfolioId}`);

// ---- Market Data API ----

export interface MarketDataPoint {
  id: number;
  symbol: string;
  date: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  adjusted_close?: number;
  volume?: number;
  source: string;
  /** Present only when `adjusted=true` was requested. */
  cumulative_factor?: number;
}

const DEFAULT_MARKET_DATA_LIMIT = 1000;

export const getMarketDataSymbols = () =>
  api.get<string[]>("/api/v1/market-data/symbols");

export const getMarketData = (params: {
  symbol: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  adjusted?: boolean;
}) => api.get<MarketDataPoint[]>("/api/v1/market-data/", { params: { ...params, limit: params.limit ?? DEFAULT_MARKET_DATA_LIMIT } });

export const fetchMarketData = (symbol: string, full = false) =>
  api.post(`/api/v1/market-data/fetch/${symbol}`, null, {
    params: { full },
  });

export const fetchAllMarketData = () =>
  api.post<{ fetched: { symbol: string; rows_upserted: number }[]; errors: { symbol: string; error: string }[] }>(
    "/api/v1/market-data/fetch-all"
  );

// ---- Position History API ----

export interface PositionHistoryPoint {
  date: string;
  quantity: number;
  market_value: number;
  market_price: number;
  cost_basis: number;
  unrealized_pnl: number;
  realized_pnl: number;
}

export const getPositionHistory = (
  portfolioId: number,
  symbol: string,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<PositionHistoryPoint[]>(
    `/api/v1/analytics/${portfolioId}/position-history/${symbol}`,
    { params }
  );

// ---- Benchmark API ----

export interface BenchmarkAllocation {
  id: number;
  benchmark_id: number;
  symbol: string;
  quantity: number;
  start_date: string;
  created_at: string;
}

export interface BenchmarkCashRate {
  id: number;
  benchmark_id: number;
  currency: string;
  amount: number;
  annual_rate: number | null;  // null → falls back to Benchmark.cash_rate
  start_date: string;
  created_at: string;
}

export interface Benchmark {
  id: number;
  portfolio_id: number;
  name: string;
  cash_rate: number;
  created_at: string;
  updated_at: string;
  allocations: BenchmarkAllocation[];
  cash_rates: BenchmarkCashRate[];
}

export interface BenchmarkEvolutionPoint {
  date: string;
  benchmark_value: number;
}

export const listBenchmarks = (portfolioId: number) =>
  api.get<Benchmark[]>(`/api/v1/benchmarks/`, { params: { portfolio_id: portfolioId } });

export const createBenchmark = (portfolioId: number, data: { name: string; cash_rate?: number }) =>
  api.post<Benchmark>(`/api/v1/benchmarks/`, data, { params: { portfolio_id: portfolioId } });

export const updateBenchmark = (id: number, data: { name?: string; cash_rate?: number }) =>
  api.patch<Benchmark>(`/api/v1/benchmarks/${id}`, data);

export const deleteBenchmark = (id: number) =>
  api.delete(`/api/v1/benchmarks/${id}`);

export const addBenchmarkAllocation = (
  benchmarkId: number,
  data: { symbol: string; quantity: number; start_date: string }
) =>
  api.post<BenchmarkAllocation>(`/api/v1/benchmarks/${benchmarkId}/allocations`, data);

export const deleteBenchmarkAllocation = (benchmarkId: number, allocationId: number) =>
  api.delete(`/api/v1/benchmarks/${benchmarkId}/allocations/${allocationId}`);

export const addBenchmarkCashRate = (
  benchmarkId: number,
  data: { currency: string; amount: number; annual_rate: number | null; start_date: string }
) =>
  api.post<BenchmarkCashRate>(`/api/v1/benchmarks/${benchmarkId}/cash-rates`, data);

export const deleteBenchmarkCashRate = (benchmarkId: number, cashRateId: number) =>
  api.delete(`/api/v1/benchmarks/${benchmarkId}/cash-rates/${cashRateId}`);

export const getBenchmarkEvolution = (
  portfolioId: number,
  benchmarkId: number,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<BenchmarkEvolutionPoint[]>(
    `/api/v1/analytics/${portfolioId}/benchmark-evolution/${benchmarkId}`,
    { params }
  );

// ---- Finnhub API ----

export interface FinnhubQuote {
  symbol: string;
  price: number;
  change: number;
  change_pct: number;
  high: number;
  low: number;
  open: number;
  prev_close: number;
}

export interface IntradayBar {
  id: number;
  symbol: string;
  timestamp: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  trade_count?: number;
  created_at: string;
}

export const getFinnhubQuote = (symbol: string) =>
  api.get<FinnhubQuote>(`/api/v1/finnhub/quote/${symbol}`);

export const getFinnhubSubscriptions = () =>
  api.get<{ symbols: string[] }>("/api/v1/finnhub/subscriptions");

export const subscribeFinnhub = (symbol: string) =>
  api.post(`/api/v1/finnhub/subscribe/${symbol}`);

export const unsubscribeFinnhub = (symbol: string) =>
  api.delete(`/api/v1/finnhub/subscribe/${symbol}`);

export const getIntradayBars = (params: {
  symbol: string;
  start?: string;
  end?: string;
  limit?: number;
}) => api.get<IntradayBar[]>("/api/v1/finnhub/intraday", { params });

export interface WsSymbolHealth {
  ticks_5min: number;
  last_tick_at: string | null;
}

export interface WsHealth {
  ws_connected: boolean;
  symbols: Record<string, WsSymbolHealth>;
}

export const getWsHealth = () =>
  api.get<WsHealth>("/api/v1/finnhub/ws-health");

// ---- Intraday Portfolio Evolution API ----

export interface IntradayEvolutionPoint {
  timestamp: string;
  total_value: number;
}

export const getIntradayEvolution = (portfolioId: number) =>
  api.get<IntradayEvolutionPoint[]>(`/api/v1/analytics/${portfolioId}/intraday-evolution`);

// ---- Intraday Position Performance API ----

export interface IntradayPositionPerformance {
  symbol: string;
  description?: string;
  quantity: number;
  latest_price: number | null;
  latest_timestamp: string;
  change_1d_pct: number | null;
  change_1d_pnl: number | null;
  change_3d_pct: number | null;
  change_3d_pnl: number | null;
  has_intraday: boolean;
}

export const getIntradayPositionPerformance = (portfolioId: number) =>
  api.get<IntradayPositionPerformance[]>(`/api/v1/analytics/${portfolioId}/intraday-position-performance`);

// ---- CPPI Recommendations API ----

export interface SymbolRecommendation {
  symbol: string;
  description?: string;
  asset_class?: string;
  is_risky: boolean;
  current_value: number;
  current_weight_in_risky: number;
  /** Latest market price used to compute share counts (position snapshot price, adj-factor-corrected). */
  current_price?: number | null;
  target_value: number;
  trade_amount: number;
  /** Signed share count: positive = buy, negative = sell. Null when price unavailable. */
  trade_shares?: number | null;
  action: "BUY" | "SELL" | "HOLD";
  has_price_data: boolean;
}

export interface CppiRecommendation {
  as_of_date: string;
  // Portfolio
  portfolio_nav: number;
  current_risky_allocation: number;
  safe_allocation: number;
  current_risky_pct: number;
  // Benchmark / floor
  benchmark_id: number;
  benchmark_name: string;
  benchmark_value: number;
  floor_pct: number;
  floor: number;
  cushion: number;
  cushion_pct: number;
  // Risk estimates
  lookback_months: number;
  obs_count: number;
  risky_mu: number;
  risky_sigma: number;
  risk_free_rate: number;
  // CPPI multiplier
  multiplier_raw: number;
  multiplier: number;
  max_multiplier: number;
  // Targets
  target_risky_allocation: number;
  target_risky_pct: number;
  target_safe_allocation: number;
  // Output
  recommendations: SymbolRecommendation[];
  data_warnings: string[];
}

export const getCppiRecommendations = (
  portfolioId: number,
  params: {
    benchmark_id: number;
    floor_pct?: number;
    lookback_months?: number;
    risk_free_rate?: number;
    max_multiplier?: number;
  }
) =>
  api.get<CppiRecommendation>(
    `/api/v1/analytics/${portfolioId}/recommendations`,
    { params }
  );

// ---- Adjustment Factors API ----

export interface AdjustmentFactor {
  id: number;
  symbol: string;
  effective_date: string;
  /** Multiplier applied to prices strictly before effective_date. */
  factor: number;
  note: string | null;
  created_at: string;
}

export const listAdjustmentFactors = (symbol?: string) =>
  api.get<AdjustmentFactor[]>("/api/v1/adjustment-factors/", {
    params: symbol ? { symbol } : undefined,
  });

export const createAdjustmentFactor = (data: {
  symbol: string;
  effective_date: string;
  factor: number;
  note?: string | null;
}) => api.post<AdjustmentFactor>("/api/v1/adjustment-factors/", data);

export const deleteAdjustmentFactor = (id: number) =>
  api.delete(`/api/v1/adjustment-factors/${id}`);

// ---- Chat API ----

export interface ChatMessage {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  cost_usd?: number | null;
  created_at: string;
}

export interface ChatSessionSummary {
  session_id: string;
  message_count: number;
  last_message_at: string;
  first_message_preview: string;
}

export interface ChatResponse {
  session_id: string;
  reply: string;
  cost_usd?: number | null;
}

export const getChatStatus = () =>
  api.get<{ enabled: boolean }>("/api/v1/chat/status");

export const listChatSessions = () =>
  api.get<ChatSessionSummary[]>("/api/v1/chat/sessions");

export const getChatSession = (sessionId: string) =>
  api.get<ChatMessage[]>(`/api/v1/chat/sessions/${sessionId}`);

export const deleteChatSession = (sessionId: string) =>
  api.delete(`/api/v1/chat/sessions/${sessionId}`);

export const sendChatMessage = (data: { message: string; session_id?: string; portfolio_id?: number }) =>
  api.post<ChatResponse>("/api/v1/chat/messages", data);

export interface IbkrFetchResult {
  account_id: string;
  trades_added: number;
  trades_skipped: number;
  positions_added: number;
  positions_updated: number;
  cash_positions_upserted: number;
  market_prices_from_flex: number;
  fx_rates_upserted: number;
}

export const fetchIbkrFlex = (portfolioId: number) =>
  api.post<IbkrFetchResult>(`/api/v1/ibkr/fetch/${portfolioId}`);
